// URL view-state codec (EPIC #157, Lane E1 — design §3.1).
//
// The single navigational entry point: the one pure place that maps between the
// browser URL's query params and the app's *view state* (what the user is looking
// at). Memory (the run-store) stays authoritative; this module is only the
// serialization, so refresh/share and Back/Forward can replicate the interface.
//
// Pure (no React, no `window`) so it unit-tests in isolation — the impure adapters
// that wire it to the store + history live in `urlSync.ts`. Unknown params are
// preserved on write and ignored on read: every param shipped is forever-forward-
// compat surface (shared links live in sent emails), so the schema grows one param
// at a time and all navigational URL access funnels through here (grep-ratcheted —
// no ad-hoc `location.search` reads outside `lib/view/`).
//
// E1 schema (active + compare only). Panels / report / event-modal follow in a
// later PR once their state moves into a store the adapter can reach — the optional
// reserved fields below mark the growth points.
import { readForecastParam, writeForecastToSearch } from "../forecast/deepLink.js";
import { readCompareFromSearch, writeCompareToSearch } from "../compareUrl.js";
import { activeSet, comparisonSet, comparisonCleared } from "../forecast/reducer.js";
import { selectActiveRun, selectAllRuns, selectRootRunId } from "../forecast/selectors.js";
import type { RunAction, RunState } from "../forecast/reducer.js";
import type { Run } from "../forecast/model.js";

/**
 * The navigational view slice, serialized to/from the URL. Ids are the run's
 * stable server `forecastId` when persisted (shareable + reload-stable), falling
 * back to the client run id before the server allocates one.
 */
export interface ViewState {
  /** Focused forecast, or null when nothing is focused. Absorbs `?forecast=`. */
  activeId: string | null;
  /** Comparison lane ids (2+ = compare mode on). Absorbs `?compare=`. */
  compareIds: string[];
  // Reserved growth points (later PR): panels?: string[]; report?: boolean; eventId?: string | null;
}

/** Parse a URL search string into a ViewState. Unknown params are ignored. */
export function parseViewParams(search: string): ViewState {
  return {
    activeId: readForecastParam(search),
    compareIds: readCompareFromSearch(search),
  };
}

/**
 * Serialize a ViewState onto `currentSearch`, setting/clearing only the
 * view-owned params and leaving any unknown params untouched. Returns a search
 * string ("?..." or "").
 */
export function serializeViewParams(view: ViewState, currentSearch = ""): string {
  const withCompare = writeCompareToSearch(currentSearch, view.compareIds);
  return writeForecastToSearch(withCompare, view.activeId);
}

/**
 * Project the run-store state into a ViewState — the "view slice" for E1 is just
 * the store's `{activeRunId, comparison}` viewed through the URL id-space
 * (client run id → stable `forecastId` where one exists).
 */
export function selectViewState(state: RunState): ViewState {
  // Cross-slice: the ACTIVE run may be one shared with the viewer, which lives in
  // `sharedRunsById`. Missing it yields `activeId: null` — no `?forecast=` at all
  // rather than a visibly wrong one.
  const active = selectActiveRun(state);
  return {
    activeId: active ? (active.forecastId ?? active.runId) : null,
    // Filter to runs present before mapping: RUN_REMOVED drops a removed run's
    // lane, but `comparisonSet` stores ids as given, and an unfiltered map would
    // fall back to the raw runId and write a ghost id into the shareable URL.
    // OWNER LEG ONLY, unlike activeId — `selectComparisonLanes` refuses a shared
    // id, so serializing one here would put a lane in a shareable URL that
    // nothing renders. Comparing a shared row is its own unbuilt feature.
    compareIds: state.comparison.rootIds
      .filter((rid) => state.runsById[rid])
      .map((rid) => state.runsById[rid]?.forecastId ?? rid),
  };
}

/**
 * Resolve a ViewState (URL id-space) into the run-store actions that reproduce it,
 * against the current runs. Deliberately view-only: it emits `activeSet` +
 * `comparisonSet`/`comparisonCleared` and NOTHING that starts/cancels/mutates a
 * run — so applying it (on popstate/boot) can never touch a running forecast.
 * Ids that resolve to no loaded run are dropped.
 *
 * Focus fallback: when there's no explicit `activeId` (or it resolves to no loaded
 * run — e.g. a legacy/shared compare-only `?compare=` link), focus the first
 * surviving compare lane. `compareIds[0]` is the focused lane by convention (see
 * `lib/compareUrl.js`; App.jsx pins `activeSet(lead.id)` when entering compare), so
 * this keeps the lead lane focused rather than clearing focus.
 */
export function viewStateActions(view: ViewState, state: RunState): RunAction[] {
  // Two resolvers, matching selectViewState's two rules. An unresolvable id is
  // DROPPED, so scanning the wrong set is a silent refusal to focus.
  //
  // Each list is built AT MOST ONCE per call, and only if some id needs it — a
  // per-id `Object.values` would be O(ids x runs) with several compare ids.
  let allRuns: Run[] | null = null;
  let ownedRuns: Run[] | null = null;
  const find = (runs: Run[], id: string): string | null =>
    runs.find((r) => r.forecastId === id || r.runId === id)?.runId ?? null;
  // `?forecast=` may name a row shared WITH the viewer — the whole point of a
  // share URL — so focus resolves cross-slice. A branch id resolves to its root, the same rule as
  // the boot deep link (SOO-862): a history entry still holding a branch link must not focus the
  // branch itself on Back/Forward.
  const toActiveRunId = (id: string): string | null => {
    const runId = find((allRuns ??= selectAllRuns(state)), id);
    return runId ? selectRootRunId(state, runId) : null;
  };
  // `?compare=` stays owner-only, for the same reason selectViewState does not
  // serialize a shared compare lane.
  const toCompareRunId = (id: string): string | null =>
    find((ownedRuns ??= Object.values(state.runsById)), id);

  // Dedupe resolved runIds (order-preserving): two different URL ids can resolve to
  // the same run (a legacy client id + its persisted forecastId), and the reducer
  // does not dedupe comparison.rootIds — so without this, compare mode could enable
  // with duplicate lanes. Mirrors the dedup in decodeCompareParam / resolveSelection.
  const compareRunIds = [
    ...new Set(view.compareIds.map(toCompareRunId).filter((id): id is string => id !== null)),
  ];
  const explicitActive = view.activeId ? toActiveRunId(view.activeId) : null;
  const activeRunId = explicitActive ?? compareRunIds[0] ?? null;

  return [
    activeSet(activeRunId),
    compareRunIds.length >= 2 ? comparisonSet(compareRunIds) : comparisonCleared(),
  ];
}

/** The entry-source query param (`?src=alert` on alert email links). */
export const ENTRY_SOURCE_PARAM = "src";

/** Entry sources a link may declare; any other `src` value reads as none. */
const DECLARED_ENTRY_SOURCES = new Set(["alert"]);

/**
 * The entry source a URL's query declares (`?src=alert` → "alert"), or null. Telemetry reads
 * it once per page load to say how a visit began (P2 design §4.5).
 */
export function readEntrySourceParam(search: string): string | null {
  const v = new URLSearchParams(search || "").get(ENTRY_SOURCE_PARAM);
  return v && DECLARED_ENTRY_SOURCES.has(v) ? v : null;
}

/**
 * `search` without the entry-source param, every other param kept in order: once consumed, a
 * reload or bookmark of the page must not read as another alert visit.
 */
export function stripEntrySourceParam(search: string): string {
  const p = new URLSearchParams(search || "");
  if (!p.has(ENTRY_SOURCE_PARAM)) return search || "";
  p.delete(ENTRY_SOURCE_PARAM);
  const rest = p.toString();
  return rest ? `?${rest}` : "";
}
